import React, { useMemo, useState } from "react";

/**
 * Rankridge Contact Page – React + Tailwind (Responsive, conversion‑focused)
 * - Hero with map + form
 * - Branch cards grid with Call / WhatsApp / Directions
 * - Sticky mobile contact bar
 * - FAQ accordion
 * - Testimonials snippet for trust
 * - Lightweight CAPTCHA (custom, no dependency)
 *
 * Drop into your React app and render <ContactPage />
 * Tailwind required. No extra libraries.
 */

const BRANCHES = [
  {
    id: "kphb",
    title: "Day Scholar Campus - KPHB Road no-1",
    address:
      "First Floor, Before Sitara Grand Hotel, Metro Station, H No - MIG 12, Rd Number 1, near KPHB, Phase 1, K P H B Colony, Kukatpally, Hyderabad, Telangana 500072",
    phone: "+919959271353",
    whatsapp: "+919959271353",
    email: "admissions@rankridgecollege.com",
    mapsUrl:
      "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2938.2565912717923!2d78.3949511!3d17.4901122!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bcb9142171bf8fd%3A0x25cda1199abf1471!2sRANKRIDGE%20-%20Best%20INTER%20College%20%26%20IITJEE%20%7C%20NEET%20%7C%20EAMCET%20Coaching.%20Top%20Junior%20College%20for%20MPC%20%7C%20BIPC%20in%20Hyderabad%20Telangana!5e1!3m2!1sen!2sin!4v1758727222314!5m2!1sen!2sin",
  },
  {
    id: "Bachupally",
    title: "Residencial Campus for Boys - Bachupally",
    address:
      "Plot NO: C-76 & C-77, DPS Rd, Simhapuri Colony, Bachupally, Hyderabad, Telangana 500043",
    phone: "+919959271353",
    whatsapp: "+919959271353",
    email: "miyapur@rankridgecollege.com",
    mapsUrl:
      "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2938.255621700026!2d78.3948557!3d17.4901722!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bcb91f1d6fd0529%3A0xe864fb01c447e247!2sRANKRIDGE%20-%20Best%20NEET%20%26%20IIT%20Coaching%20in%20Hyderabad!5e1!3m2!1sen!2sin!4v1758790613553!5m2!1sen!2sin",
  },
  {
    id: "srnagar",
    title: "Residential Campus for Girls - Gokul X Roads",
    address: "Plot No 531 and 540 Forum Sujana Mall lane, Gokul X Road opposite SBI lane, Salivahana Colony, Kukatpally Housing Board Colony, Kukatpally, Hyderabad, Telangana 500085",
    phone: "+917799003456",
    whatsapp: "+917799003456",
    email: "srnagar@rankridgecollege.com",
    mapsUrl:
      "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2938.2906874455794!2d78.3809122!3d17.488002099999996!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bcb93e20c02a971%3A0x2cf6230ef209dfdc!2sRANKRIDGE%20-%20Best%20NEET%20Long%20Term%20Coaching%20in%20Hyderabad!5e1!3m2!1sen!2sin!4v1758790468742!5m2!1sen!2sin",
  },
];

const FAQs = [
  {
    q: "What are the office hours?",
    a: "Monday to Saturday, 9:30 AM – 6:30 PM (IST). Sundays by appointment for admissions counseling.",
  },
  {
    q: "How soon will I get a response?",
    a: "We usually respond within 24 hours on business days. For urgent queries, please call the admissions helpline.",
  },
  {
    q: "Do you offer hostel facilities?",
    a: "Yes. Separate boys’ & girls’ hostels with 24/7 security, wardens, CCTV, and dietician‑curated meals.",
  },
  {
    q: "Can I book a campus visit?",
    a: "Yes. Use the form to request a preferred date/time, or call us to schedule a guided tour.",
  },
];

function Captcha({ onChange }) {
  // Simple human check: show expression like "8 + 4" -> expect 12
  const { a, b } = useMemo(() => ({ a: rand(3, 9), b: rand(2, 9) }), []);
  const [input, setInput] = useState("");

  function handle(e) {
    const v = e.target.value.replace(/[^0-9]/g, "");
    setInput(v);
    onChange?.(Number(v) === a + b);
  }

  return (
    <div className="grid gap-2">
      <label htmlFor="captcha" className="text-sm font-medium text-slate-700">
        Human check: What is {a} + {b}?
      </label>
      <input
        id="captcha"
        inputMode="numeric"
        pattern="[0-9]*"
        value={input}
        onChange={handle}
        className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-slate-900 outline-none ring-emerald-300 transition placeholder:text-slate-400 focus:ring"
        placeholder="Type the answer"
        required
        aria-describedby="captcha-help"
      />
      {/* <p id="captcha-help" className="text-xs text-slate-500">
        This helps us prevent spam.
      </p> */}
    </div>
  );
}

export default function ContactPage() {
  const [captchaOk, setCaptchaOk] = useState(false);
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState(null); // {type: 'ok' | 'err', msg}

  function onSubmit(e) {
    e.preventDefault();
    if (!captchaOk) {
      setStatus({ type: "err", msg: "Please solve the human check correctly." });
      return;
    }
    const form = new FormData(e.currentTarget);

    // Simulate submit (replace with your API call)
    setSending(true);
    setTimeout(() => {
      setSending(false);
      setStatus({ type: "ok", msg: "Thanks! We will get back to you within 24 hours." });
      e.currentTarget.reset();
      setCaptchaOk(false);
    }, 900);
  }

  return (
    <div className="bg-gradient-to-b from-white to-slate-50">
      {/* HERO: Map + Form */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid gap-8 lg:grid-cols-12">
          {/* Map */}
          <div className="lg:col-span-6">
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-100 px-4 py-1 text-sm font-semibold text-slate-700">
                Find Us on the Map
              </div>
              <div className="aspect-[16/9] mb-1">
                <iframe
                  title="Rankridge Map"
                  src={BRANCHES[0].mapsUrl}
                  className="h-full w-full"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>

                <div className="aspect-[16/9]">
                <iframe
                  title="Rankridge Map"
                  src={'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3805.3629060516523!2d78.3948341!3d17.4901814!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bcb915ba16b6a93%3A0x89777823a0ce54cf!2sRankridge%20Schools%20(%20Nursery%20-%2010th%20Class%20)%20-%20Best%20Schools%20in%20Hyderabad%20with%20IIT%20JEE%20%26%20NEET%20Foundation!5e0!3m2!1sen!2sin!4v1758791300788!5m2!1sen!2sin'}
                  className="h-full w-full"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-6">
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h1 className="text-2xl font-bold tracking-tight text-slate-900">
                Contact Admissions
              </h1>
              <p className="mt-2 text-sm text-slate-600">
                Have questions about IIT‑JEE/NEET long‑term programs, hostels, or fees? We’ll help you with the right guidance.
              </p>

              <form onSubmit={onSubmit} className="mt-6 grid gap-4">
                <div className="grid gap-2">
                  <label htmlFor="name" className="text-sm font-medium text-slate-700">
                    Full Name
                  </label>
                  <input
                    id="name"
                    name="name"
                    required
                    autoComplete="name"
                    placeholder="Your name"
                    className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-slate-900 outline-none ring-emerald-300 transition placeholder:text-slate-400 focus:ring"
                  />
                </div>

                <div className="grid gap-2 sm:grid-cols-2">
                  <div className="grid gap-2">
                    <label htmlFor="phone" className="text-sm font-medium text-slate-700">
                      Phone
                    </label>
                    <input
                      id="phone"
                      name="phone"
                      inputMode="tel"
                      autoComplete="tel"
                      placeholder="e.g., 9959271353"
                      required
                      className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-slate-900 outline-none ring-emerald-300 transition placeholder:text-slate-400 focus:ring"
                    />
                  </div>
                  <div className="grid gap-2">
                    <label htmlFor="email" className="text-sm font-medium text-slate-700">
                      Email (optional)
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="you@example.com"
                      className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-slate-900 outline-none ring-emerald-300 transition placeholder:text-slate-400 focus:ring"
                    />
                  </div>
                </div>

                <div className="grid gap-2 sm:grid-cols-1">
                  <div className="grid gap-2">
                    <label htmlFor="interest" className="text-sm font-medium text-slate-700">
                      I’m interested in
                    </label>
                    <select
                      id="interest"
                      name="interest"
                      className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-slate-900 outline-none ring-emerald-300 transition focus:ring"
                      defaultValue="IIT-JEE"
                    >
                      <option>IIT-JEE</option>
                      <option>NEET</option>
                      <option>Foundation (Grade 8–10)</option>
                      <option>Other</option>
                    </select>
                  </div>
                  {/* <div className="grid gap-2">
                    <label htmlFor="branch" className="text-sm font-medium text-slate-700">
                      Preferred Branch
                    </label>
                    <select
                      id="branch"
                      name="branch"
                      className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-slate-900 outline-none ring-emerald-300 transition focus:ring"
                      defaultValue={BRANCHES[0].title}
                    >
                      {BRANCHES.map((b) => (
                        <option key={b.id}>{b.title}</option>
                      ))}
                    </select>
                  </div> */}
                </div>

                <div className="grid gap-2">
                  <label htmlFor="message" className="text-sm font-medium text-slate-700">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={4}
                    placeholder="Tell us about your query (admissions, fees, hostel, campus visit…)"
                    className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-slate-900 outline-none ring-emerald-300 transition placeholder:text-slate-400 focus:ring"
                  />
                </div>

                <Captcha onChange={setCaptchaOk} />

                <div className="flex items-center justify-center">
                  {/* <p className="text-xs text-slate-500">We typically reply within 24 hours.</p> */}
                  <button
                    type="submit"
                    disabled={sending}
                    className="inline-flex items-center gap-2 rounded-2xl bg-[#20b2aa] px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:shadow-md disabled:opacity-60"
                  >
                    {sending ? "Sending…" : "Send Enquiry"}
                  </button>
                </div>

                {status && (
                  <div
                    className={`rounded-xl px-3 py-2 text-sm ${
                      status.type === "ok"
                        ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
                        : "bg-rose-50 text-rose-700 ring-1 ring-rose-200"
                    }`}
                    role="status"
                    aria-live="polite"
                  >
                    {status.msg}
                  </div>
                )}
              </form>

              {/* Quick contacts */}
              {/* <div className="mt-6 grid gap-2 text-sm text-slate-700">
                <a href="tel:+919959271353" className="hover:underline">
                  📞 Admissions Helpline: +91 99 59 271 353
                </a>
                <a
                  href="https://wa.me/919959271353"
                  target="_blank"
                  rel="noreferrer"
                  className="hover:underline"
                >
                  💬 WhatsApp: Chat with us
                </a>
                <a href="mailto:admissions@rankridgecollege.com" className="hover:underline">
                  ✉️ Email: admissions@rankridgecollege.com
                </a>
              </div> */}
            </div>
          </div>
        </div>
      </section>

      {/* BRANCH GRID */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pb-6">
        <div className="text-center">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
            Branches & Contact
          </h2>
          <p className="mt-2 text-slate-600">Call, WhatsApp, or get directions in one tap.</p>
        </div>

        <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {BRANCHES.map((b) => (
            <div key={b.id} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h3 className="text-base font-semibold text-slate-900">{b.title}</h3>
              <p className="mt-2 text-sm text-slate-600">{b.address}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                <a
                  href={`tel:${b.phone}`}
                  className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  Call
                </a>
                <a
                  href={`https://wa.me/${b.whatsapp.replace(/[^0-9]/g, "")}`}
                  target="_blank"
                  rel="noreferrer"
                  className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-700 hover:bg-emerald-100"
                >
                  WhatsApp
                </a>
                <a
                  href={b.mapsUrl.replace("&output=embed", "")}
                  target="_blank"
                  rel="noreferrer"
                  className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  Directions
                </a>
              </div>
              <div className="mt-4 overflow-hidden rounded-xl ring-1 ring-slate-200">
                <iframe title={`${b.title} Map`} src={b.mapsUrl} className="h-40 w-full" loading="lazy" />
              </div>
            </div>
          ))}
        </div>
      </section>

      

      {/* STICKY CONTACT BAR (Mobile‑first) */}
      <nav className="fixed inset-x-0 bottom-0 z-50 block bg-white/90 backdrop-blur md:hidden">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-2 px-4 py-2">
          <a
            href="tel:+919959271353"
            className="flex-1 rounded-xl border border-slate-300 bg-white px-3 py-2 text-center text-sm font-semibold text-slate-800"
          >
            Call
          </a>
          <a
            href="https://wa.me/919959271353"
            target="_blank"
            rel="noreferrer"
            className="flex-1 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-center text-sm font-semibold text-emerald-700"
          >
            WhatsApp
          </a>
          <a
            href={BRANCHES[0].mapsUrl.replace("&output=embed", "")}
            target="_blank"
            rel="noreferrer"
            className="flex-1 rounded-xl border border-slate-300 bg-white px-3 py-2 text-center text-sm font-semibold text-slate-800"
          >
            Directions
          </a>
          <a href="#top" className="flex-1 rounded-xl bg-[#20b2aa] px-3 py-2 text-center text-sm font-semibold text-white">
            Enquiry
          </a>
        </div>
      </nav>
    </div>
  );
}

function rand(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}
